import { createReducer, on } from '@ngrx/store';
import * as pageActions from '../actions/products.action';
import * as utility from '../../../utility';
import { FlattenGraphQl } from '../../../utility/graphql-flatten.utility';
import { Product } from '../../models';

export interface ProductsState {
  entities: { [id: number]: Product };
  detailPageEntities: string[];
  filteredEntities: { [id: number]: Product };
  queriedEntities: {
    [id: string]: { [id: number]: Product };
  };
  recommendations: Product[];
  recommendationsFor: string;
  recommendationsLoading: boolean;
  collectionHandle?: string;
  searchQuery?: string;
  pageInfo?: {
    cursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filteredPageInfo?: {
    cursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  loaded: boolean;
  loading: boolean;
  error?: any;
  sectionStates: { [identifier: string]: { loaded: boolean; loading: boolean } };
}

export const initialState: ProductsState = {
  entities: {},
  detailPageEntities: [],
  filteredEntities: {},
  queriedEntities: {},
  recommendations: [],
  recommendationsFor: '',
  recommendationsLoading: false,
  pageInfo: {
    cursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  filteredPageInfo: {
    cursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  },
  collectionHandle: undefined,
  loaded: false,
  loading: false,
  searchQuery: '',
  sectionStates: {},
};

export const ProductReducer = createReducer(
  initialState,
  on(pageActions.LoadProducts, (state: ProductsState) => {
    return {
      ...state,
      collectionHandle: undefined,
      loaded: false,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsSuccess, (state: ProductsState, { Products }) => {
    const prods = Products?.edges.map((e: { node: any }) => FlattenGraphQl(e.node));
    const entities = utility.ToEntities(prods, 'handle', state.entities);
    return {
      ...state,
      entities,
      pageInfo: {
        cursor: Products?.pageInfo.endCursor || Products?.edges[Products?.edges.length - 1]?.cursor || state?.pageInfo?.cursor,
        hasNextPage: Products?.pageInfo.hasNextPage,
        hasPreviousPage: Products?.pageInfo.hasPreviousPage,
      },
      loading: false,
      loaded: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: false,
      error,
    };
  }),
  on(pageActions.LoadMoreProductsForCollection, pageActions.LoadMoreProductsFiltered, (state: ProductsState) => {
    return {
      ...state,
      loaded: false,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsForCollection, (state: ProductsState, { handle }) => {
    return {
      ...state,
      entities: {},
      filteredEntities: {},
      detailPageEntities: [],
      collectionHandle: handle,
      filteredPageInfo: initialState.filteredPageInfo,
      searchQuery: undefined,
      loaded: false,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsForCollectionSuccess, (state: ProductsState, { Products }) => {
    const prods = Products?.edges.map((e: { node: any }) => FlattenGraphQl(e.node));
    const entities = utility.ToEntities(prods, 'handle', state.entities);
    return {
      ...state,
      entities,
      pageInfo: {
        cursor: Products?.pageInfo.endCursor || Products?.edges[Products?.edges.length - 1]?.cursor || state?.pageInfo?.cursor || '',
        hasNextPage: Products?.pageInfo.hasNextPage,
        hasPreviousPage: Products?.pageInfo.hasPreviousPage,
      },
      loading: false,
      loaded: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsFiltered, (state: ProductsState, { handle }) => {
    return {
      ...state,
      collectionHandle: handle,
      searchQuery: undefined,
      loaded: false,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadFilterdProductsByQuery, (state: ProductsState, { searchQuery }) => {
    return {
      ...state,
      collectionHandle: undefined,
      searchQuery,
      loaded: false,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductsFilteredSuccess, (state: ProductsState, { Products, handle, searchQuery, loadMore }) => {
    const prods = Products?.edges.map((e: { node: any }) => FlattenGraphQl(e.node));
    const entities = utility.ToEntities(prods, 'id', {});

    return {
      ...state,
      filteredEntities: loadMore ? { ...state.filteredEntities, ...entities } : entities,
      collectionHandle: handle,
      searchQuery: searchQuery,
      filteredPageInfo: {
        cursor: Products?.pageInfo.endCursor || Products?.edges[Products?.edges.length - 1]?.cursor || state?.pageInfo?.cursor || '',
        hasNextPage: Products?.pageInfo.hasNextPage,
        hasPreviousPage: Products?.pageInfo.hasPreviousPage,
      },
      loading: false,
      loaded: true,
    };
  }),
  on(pageActions.LoadProductsFilteredFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: false,
      error,
    };
  }),
  on(pageActions.LoadProductsFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: false,
      error,
    };
  }),
  on(pageActions.LoadProductByHandle, (state: ProductsState) => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(pageActions.LoadProductByHandleSuccess, (state: ProductsState, { product, loadDetails, handle }) => {
    const entities = { ...state.entities, [handle]: { ...FlattenGraphQl(product), originalHandle: handle } };
    let detailPageEntities = [...state.detailPageEntities];

    // Check if details are loaded
    if (loadDetails) {
      detailPageEntities = [...state.detailPageEntities, handle];
    }

    return {
      ...state,
      entities,
      detailPageEntities,
      loading: false,
    };
  }),
  on(pageActions.LoadProductByHandleFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      loading: false,
      error,
    };
  }),
  on(pageActions.LoadProductsByQuery, (state: ProductsState, { searchQuery, identifier }) => {
    return {
      ...state,
      queriedEntities: { ...state.queriedEntities, ['global-search']: [], [identifier]: [] },
      loaded: false,
      loading: true,
      error: null,
      searchQuery: identifier === 'global-search' ? searchQuery : state.searchQuery,
      sectionStates: { ...state.sectionStates, [identifier]: { loaded: false, loading: true } },
    };
  }),
  on(pageActions.EmptyGlobalProductsSearch, (state: ProductsState) => {
    return {
      ...state,
      queriedEntities: { ...state.queriedEntities, ['global-search']: [] },
      searchQuery: '',
      loaded: true,
      loading: false,
    };
  }),
  on(pageActions.LoadProductsByQuerySuccess, (state: ProductsState, { products, identifier }) => {
    const prods = products?.edges.map((e: { node: any }) => FlattenGraphQl(e.node));
    const entities = utility.ToEntities(prods, 'id', {});

    // if detail is already loaded don't overwrite it
    const newProds = prods.filter((e: Product) => !state.detailPageEntities.includes(e.handle));
    const newEntities = utility.ToEntities(newProds, 'handle', {});

    // this fixes a bug where products overide the allready present products and deletes title/image etc.
    // Because the incomming products do not have all the properties, so we merge them to prevent loss of data
    const mergedEntities = { ...state.entities };
    for (const key in newEntities) {
      if (newEntities.hasOwnProperty(key)) {
        const existingEntity = mergedEntities[key];
        const incomingEntity = newEntities[key];

        mergedEntities[key] = { ...existingEntity, ...incomingEntity };
      }
    }

    return {
      ...state,
      entities: mergedEntities,
      queriedEntities: { ...state.queriedEntities, [identifier]: entities },
      loaded: true,
      loading: false,
      sectionStates: { ...state.sectionStates, [identifier]: { loaded: true, loading: false } },
    };
  }),
  on(pageActions.LoadProductsByQueryFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: false,
      error,
    };
  }),
  on(pageActions.LoadProductRecommendations, (state: ProductsState, { productId }) => {
    return {
      ...state,
      recommendations: [],
      recommendationsLoading: true,
      recommendationsFor: '',
      error: null,
    };
  }),
  on(pageActions.LoadProductRecommendationsSuccess, (state: ProductsState, { Products, productId }) => {
    const recommendations = Products?.map((e: { node: any }) => FlattenGraphQl(e));

    return {
      ...state,
      recommendations,
      recommendationsLoading: false,
      recommendationsFor: productId,
    };
  }),
  on(pageActions.LoadProductRecommendationsFail, (state: ProductsState, { error }) => {
    return {
      ...state,
      recommendations: [],
      recommendationsLoading: false,
      recommendationsFor: '',
    };
  })
);

export const getProductsEntities = (state: ProductsState) => state.entities;
export const getProductsDetailEntities = (state: ProductsState) => state.detailPageEntities;
export const getProductsLoading = (state: ProductsState) => state.loading;
export const getProductsCollectionHandle = (state: ProductsState) => state.collectionHandle;
export const getProductsPageInfo = (state: ProductsState) => state.pageInfo;
export const getProductsFilteredPageInfo = (state: ProductsState) => state.filteredPageInfo;
export const getProductsLoaded = (state: ProductsState) => state.loaded;

export const getProductSectionStates = (state: ProductsState) => state.sectionStates;

// TODO
export const getProductsQueried = (state: ProductsState) => state.queriedEntities;
export const getProductsFiltered = (state: ProductsState) => state.filteredEntities;

export const getRecommendations = (state: ProductsState) => state.recommendations;
export const getRecommendationsFor = (state: ProductsState) => state.recommendationsFor;
export const getRecommendationsLoading = (state: ProductsState) => state.recommendationsLoading;

export const getProductError = (state: ProductsState) => state.error;
export const getProductSearchQuery = (state: ProductsState) => state.searchQuery;
